import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import ErrorBoundary from "~/components/ErrorBoundry";
import { fetchFile } from "~/store/actions/actions.files.jsx";
import { getFileById } from "~/store/selectors/selector.files.jsx";
import { FilePreviewContextProvider } from "./context/index.jsx";
import { FilePreviewModal } from "./FilePreviewModal.jsx";
import { FilesRealtime } from "../Folders/FilesRealtime.js";

const initialFilePreviewState = {
  open: false,
  id: 0,
  files: [],
};

export const FilePreviewProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [state, setState] = useState(initialFilePreviewState);

  const [searchParams, setSearchParams] = useSearchParams();
  const filePreview = searchParams?.get("filePreview") ?? "";
  const [firstValue, secondValue] = filePreview.split("-");
  const fileId = Number(firstValue ?? 0);
  const projectId = Number(secondValue ?? 0);

  const openFilePreview = useCallback((id = 0, files = []) => setState({ id, files, open: true }), []);

  const closeFilePreview = useCallback(() => {
    searchParams.delete("filePreview");
    setSearchParams(searchParams);
    setState({ open: false, id: 0, files: [] });
  }, [setSearchParams, searchParams]);

  const fileRaw = useSelector((state) => getFileById(state, fileId));
  const file = useMemo(() => fileRaw?.toJS(), [fileRaw]);

  const resolveDependency = useCallback(async () => {
    return await dispatch(fetchFile(fileId, projectId));
  }, [dispatch, fileId, projectId]);

  useEffect(() => {
    if (fileId > 0) {
      file ? openFilePreview(fileId, [file]) : resolveDependency();
    }
  }, [file, fileId, openFilePreview, resolveDependency]);

  return (
    <FilePreviewContextProvider
      value={{
        openFilePreview,
        closeFilePreview,
      }}
    >
      {children}
      <ErrorBoundary
        errorLocation="FilePreviewProvider"
        key={state.id}
      >
        <FilePreviewModal
          open={state.open}
          onClose={closeFilePreview}
          files={state.files}
          currentId={state.id}
        />
        <FilesRealtime />
      </ErrorBoundary>
    </FilePreviewContextProvider>
  );
};

FilePreviewProvider.propTypes = {
  children: PropTypes.node,
};
